<template>
	<div class="ks-videoeditor-timeline">
		<timeline-toolbar
			:can_split="can_split"
			:can_delete="can_delete"
			:can_clear="can_clear"
			:zoom="zoom"
			@split="$emit('split')"
			@delete="$emit('delete')"
			@clear="$emit('clear')"
			@add-track="$emit('add-track', $event)"
			@update:zoom="$emit('update:zoom', $event)"></timeline-toolbar>

		<div class="ks-videoeditor-scroll" ref="scroll">
			<div class="ks-videoeditor-inner" :style="{width: (inner_width + head_width) + 'px'}">
				<timeline-ruler ref="ruler" :zoom="zoom" :head_width="head_width" :width="inner_width" @scrub-down="scrub_start"></timeline-ruler>

				<div v-for="row in rows" :key="row.id" class="ks-videoeditor-track-row" :class="['ks-videoeditor-row-' + row.kind, {dragging: drag_track_id == row.id, 'track-off': row.hidden}]">
					<track-header
						:track="row"
						:width="head_width"
						:active="row.id == active_track[row.kind]"
						:removable="kind_count(row.kind) > 1"
						@drag-start="track_drag_start(row, $event)"
						@rename="$emit('rename-track', row.id, $event)"
						@toggle-hidden="$emit('toggle-track', row.id)"
						@toggle-mute="$emit('toggle-track-mute', row.id)"
						@remove="$emit('remove-track', row.id)"></track-header>

					<video-track
						v-if="row.kind == 'video'"
						ref="tracks"
						:track="row"
						:zoom="zoom"
						:lane_height="lane_height"
						:follow="follow"
						:selected_id="selected_id"
						:pair_id="pair_id"
						@scrub-down="scrub_start"
						@background-click="$emit('clear-selection')"
						@clip-down="clip_move_start"
						@clip-click="clip_click"
						@trim-down="trim_start"
						@transition-click="$emit('select-transition', $event)"></video-track>
					<overlay-track
						v-else-if="row.kind == 'overlay'"
						ref="tracks"
						:track="row"
						:zoom="zoom"
						:lane_height="lane_height"
						:follow="follow"
						:selected_id="selected_overlay_id"
						@scrub-down="scrub_start"
						@background-click="$emit('clear-selection')"
						@item-down="overlay_drag_start"></overlay-track>
					<audio-track
						v-else
						ref="tracks"
						:track="row"
						:zoom="zoom"
						:lane_height="lane_height"
						:follow="follow"
						:selected_id="selected_audio_id"
						@scrub-down="scrub_start"
						@background-click="$emit('clear-selection')"
						@item-down="audio_drag_start"></audio-track>
				</div>

				<div class="ks-videoeditor-playhead" ref="playhead_line" :style="{left: (playhead * zoom + head_width) + 'px'}"></div>
			</div>
		</div>
	</div>
</template>

<script>
import {DEFAULT_VIEW_SECONDS, HEAD_WIDTH, LANE_HEIGHT, MIN_LEN} from './constants.js'
import {build_track_rows, clip_bounds, fit_start} from './layout.js'
import AudioTrack from './AudioTrack.vue'
import OverlayTrack from './OverlayTrack.vue'
import TimelineRuler from './TimelineRuler.vue'
import TimelineToolbar from './TimelineToolbar.vue'
import TrackHeader from './TrackHeader.vue'
import VideoTrack from './VideoTrack.vue'

// Tracks with their clips, the ruler and the playhead. Pointer handling (move, trim, scrub, reorder) lives here;
// changes are emitted as update(item, patch) and the other events tell the editor what to select or do.
export default {
	components: {AudioTrack, OverlayTrack, TimelineRuler, TimelineToolbar, TrackHeader, VideoTrack},
	props: {
		// Tracks front to back; timeline holds the video clips, layout their playback positions
		tracks: Array,
		timeline: Array,
		layout: Array,
		overlays: Array,
		audios: Array,
		end_time: Number,
		playhead: Number,
		// Pixels per second
		zoom: Number,
		selected_id: Number,
		pair_id: Number,
		selected_overlay_id: Number,
		selected_audio_id: Number,
		active_track: Object,
		can_split: Boolean,
		can_delete: Boolean,
		can_clear: Boolean
	},
	data () {
		return {
			head_width: HEAD_WIDTH,
			lane_height: LANE_HEIGHT,
			// Item being moved with the pointer: {kind, id, grab_y, y}
			follow: null,
			drag_track_id: undefined
		}
	},
	created () {
		this.drag = undefined;
	},
	mounted () {
		this.$nextTick(this.fit_zoom);
	},
	beforeDestroy () {
		this.detach_mouse();
	},
	computed: {
		rows () {
			return build_track_rows(this.tracks, this.layout, this.overlays, this.audios);
		},
		inner_width () {
			return Math.max(this.end_time + 15, DEFAULT_VIEW_SECONDS) * this.zoom;
		}
	},
	methods: {
		kind_count (kind) {
			return _.filter(this.tracks, {kind: kind}).length;
		},
		fit_zoom () {
			const width = this.$refs.scroll ? this.$refs.scroll.clientWidth - this.head_width : 0;
			if (width > 0) this.$emit("update:zoom", _.clamp(Math.round(width / DEFAULT_VIEW_SECONDS * 10) / 10, 1, 200));
		},
		// While playing the playhead is not reactive; the editor moves the line directly
		show_playhead (time) {
			const line = this.$refs.playhead_line;
			if (line) line.style.left = (time * this.zoom + this.head_width) + "px";
		},
		change (item, patch) {
			this.$emit("update", item, patch);
		},

		// ---- Tracks
		track_els () {
			return _.map(this.$refs.tracks || [], (track) => track.$el);
		},
		track_at (kind, y) {
			const els = _.sortBy(_.filter(this.track_els(), (el) => el.dataset.kind == kind), (el) => el.getBoundingClientRect().top);
			if (!els.length) return undefined;
			const hit = _.find(els, (el) => y <= el.getBoundingClientRect().bottom);
			return (hit || _.last(els)).dataset.id;
		},
		track_drag_start (track, event) {
			this.drag = {type: "track", id: track.id, kind: track.kind, y: event.clientY, moved: false};
			this.attach_mouse();
		},
		move_track_drag (event) {
			const d = this.drag;
			if (!d.moved && Math.abs(event.clientY - d.y) < 4) return;
			d.moved = true;
			this.drag_track_id = d.id;
			const els = _.sortBy(this.track_els(), (el) => el.getBoundingClientRect().top);
			if (!els.length) return;
			const hit = _.find(els, (el) => event.clientY <= el.getBoundingClientRect().bottom) || _.last(els);
			if (hit.dataset.id != d.id) this.$emit("move-track", d.id, hit.dataset.id);
		},

		// ---- Item pointer handling
		// While an item is moved it follows the pointer vertically
		start_follow (kind, id, event) {
			const rect = event.currentTarget.getBoundingClientRect();
			this.follow = {kind: kind, id: id, grab_y: event.clientY - rect.top, y: event.clientY};
		},
		clip_click (clip, event) {
			if (!event.shiftKey || this.selected_id === undefined) this.$emit("select-clip", clip);
		},
		clip_move_start (clip, event) {
			// Shift-click picks a second clip for a transition between the two
			if (event.shiftKey && this.selected_id !== undefined && this.selected_id != clip.id) {
				this.$emit("pair-clip", clip);
				return;
			}
			this.$emit("select-clip", clip);
			this.drag = {type: "clip_move", clip: clip, x: event.clientX, start: clip.start};
			this.start_follow("video", clip.id, event);
			this.attach_mouse();
		},
		trim_start (clip, side, event) {
			this.$emit("select-clip", clip);
			this.$emit("pause");
			const bounds = clip_bounds(this.timeline, clip);
			this.drag = {type: "trim", clip: clip, side: side, x: event.clientX, start: clip.start, in: clip.in, out: clip.out, prev_end: bounds.prev_end, next_start: bounds.next_start};
			this.attach_mouse();
			this.$emit("trim", {clip: clip, side: side});
		},
		overlay_drag_start (overlay, mode, event) {
			this.$emit("select-overlay", overlay);
			this.drag = {type: "overlay", mode: mode, overlay: overlay, x: event.clientX, start: overlay.start, duration: overlay.duration};
			if (mode == "move") this.start_follow("overlay", overlay.id, event);
			this.attach_mouse();
		},
		audio_drag_start (audio, mode, event) {
			this.$emit("select-audio", audio);
			this.drag = {type: "audio", mode: mode, audio: audio, x: event.clientX, start: audio.start, in: audio.in, duration: audio.duration};
			if (mode == "move") this.start_follow("audio", audio.id, event);
			this.attach_mouse();
		},
		scrub_start (event) {
			this.$emit("pause");
			this.drag = {type: "scrub"};
			this.scrub_to(event);
			this.attach_mouse();
		},
		scrub_to (event) {
			this.$emit("scrub", this.$refs.ruler.time_at(event.clientX));
		},

		attach_mouse () {
			window.addEventListener("mousemove", this.on_move);
			window.addEventListener("mouseup", this.on_up);
		},
		detach_mouse () {
			window.removeEventListener("mousemove", this.on_move);
			window.removeEventListener("mouseup", this.on_up);
		},
		on_move (event) {
			const d = this.drag;
			if (!d) return;
			if (d.type == "track") {
				this.move_track_drag(event);
				return;
			}
			if (d.type == "scrub") {
				this.scrub_to(event);
				return;
			}
			const delta = (event.clientX - d.x) / this.zoom;
			if (this.follow) this.follow.y = event.clientY;
			if (d.type == "audio") this.move_audio(d, delta, event);
			else if (d.type == "overlay") this.move_overlay(d, delta, event);
			else if (d.type == "clip_move") this.move_clip(d, delta, event);
			else this.move_trim(d, delta);
		},
		move_audio (d, delta, event) {
			const a = d.audio;
			if (d.mode == "move") {
				const patch = {start: Math.max(0, d.start + delta)};
				const track = this.track_at("audio", event.clientY);
				if (track !== undefined) patch.track = track;
				this.change(a, patch);
			} else if (d.mode == "start") {
				// Trimming the left edge moves the source offset with it
				const shift = _.clamp(delta, -Math.min(d.start, d.in), d.duration - MIN_LEN);
				this.change(a, {start: d.start + shift, in: d.in + shift, duration: d.duration - shift});
			} else {
				this.change(a, {duration: _.clamp(d.duration + delta, MIN_LEN, a.source_duration - d.in)});
			}
		},
		move_overlay (d, delta, event) {
			const o = d.overlay;
			if (d.mode == "move") {
				const patch = {start: Math.max(0, d.start + delta)};
				const track = this.track_at("overlay", event.clientY);
				if (track !== undefined) patch.track = track;
				this.change(o, patch);
			} else if (d.mode == "start") {
				const end = d.start + d.duration;
				const start = _.clamp(d.start + delta, 0, end - MIN_LEN);
				this.change(o, {start: start, duration: end - start});
			} else {
				this.change(o, {duration: Math.max(MIN_LEN, d.duration + delta)});
			}
		},
		move_clip (d, delta, event) {
			const c = d.clip;
			const track = this.track_at("video", event.clientY) || c.track;
			this.change(c, {track: track, start: fit_start(this.timeline, c, track, Math.max(0, d.start + delta))});
		},
		move_trim (d, delta) {
			const clip = d.clip;
			if (d.side == "in") {
				// The right edge stays where it is, so the start moves with the in point
				const len = d.out - d.in;
				const room = d.start - d.prev_end;
				if (clip.type == "image") {
					const shift = _.clamp(delta, -room, len - MIN_LEN);
					this.change(clip, {start: d.start + shift, out: clip.in + len - shift});
				} else {
					const shift = _.clamp(delta, -Math.min(room, d.in), len - MIN_LEN);
					this.change(clip, {start: d.start + shift, in: d.in + shift});
				}
			} else {
				const room = d.next_start - clip.start;
				this.change(clip, {out: _.clamp(d.out + delta, clip.in + MIN_LEN, Math.min(clip.source_duration, clip.in + room))});
			}
		},
		on_up () {
			const d = this.drag;
			if (d && d.type == "track") {
				// A click without movement makes the track the active one for new items
				if (!d.moved) this.$emit("activate-track", d.kind, d.id);
				this.drag_track_id = undefined;
			}
			if (d && d.type == "trim") this.$emit("trim", null);
			this.drag = undefined;
			this.follow = null;
			this.detach_mouse();
			this.$emit("drag-end");
		}
	}
}
</script>

<style src="./timeline.css"></style>
